import { APIError } from '../../../../errors/APIError';
import { InvalidExternalAuthError } from '../../../../errors/InvalidExternalAuthError';
import { LinkCredentialError } from '../../../../errors/LinkCredentialError';
import { MfaInvalidOtpError } from '../../../../errors/MfaInvalidOtpError';
import { MfaRateLimitedError } from '../../../../errors/MfaRateLimitedError';
import { SandboxMaximumThresholdReachedError } from '../../../../errors/SandboxMaximumThresholdReachedError';
import type { ErrorMapper } from '../../apiClient.types';

/**
 * Default error mapper for the client that handles common API error codes.
 *
 * This mapper transforms specific API error codes into more specific error types:
 * - `mfa_invalid_code` → `MfaInvalidOtpError`
 * - `mfa_rate_limited` → `MfaRateLimitedError`
 *
 * @param error - The error to be mapped
 * @returns A transformed error if the error code matches a known pattern, or null if no transformation is needed
 *
 * @example
 * ```typescript
 * // This will be automatically applied to all API errors
 * const apiClient = createApiClient({}, client);
 *
 * // The clientErrorMapper will automatically convert mfa_invalid_code errors
 * // to MfaInvalidOtpError instances
 * ```
 */
export const clientErrorMapper: ErrorMapper = (error) => {
  if (error instanceof APIError) {
    if (error.code === 'mfa_invalid_code') {
      return new MfaInvalidOtpError({ cause: error });
    }

    if (error.code === 'mfa_rate_limited') {
      return new MfaRateLimitedError({ cause: error });
    }

    if (error.code === 'invalid_external_auth') {
      return new InvalidExternalAuthError({ cause: error });
    }

    if (error.code === 'sandbox_maximum_threshold_reached') {
      return new SandboxMaximumThresholdReachedError({ cause: error });
    }

    if (
      error.code === 'merge_accounts_invalid' ||
      error.code === 'reassign_wallet_error'
    ) {
      return new LinkCredentialError({ cause: error });
    }
  }

  return null;
};
